import {Component, HostListener, ViewChild} from '@angular/core';
import {Router} from '@angular/router';
import {OverlayPanel} from 'primeng/overlaypanel';
import {LazyLoadEvent} from 'primeng/api';
import Swal from 'sweetalert2';
import {UserService} from '../../services/user/user.service';
import {User} from '../../models/user';

@Component({
    selector: 'app-agents-list',
    templateUrl: './agents-list.component.html',
    styleUrls: ['../admin-allusers/admin-allusers.component.scss', './agents-list.component.scss']
})
export class AgentsListComponent {
    @ViewChild('filtersPanel') filtersPanel?: OverlayPanel;

    agents: User[] = [];
    totalRecords = 0;
    loading = true;
    first = 0;
    rows = 30;

    textFilter = '';
    selectedStatusIds: string[] = [];
    sortBy = 'firstName asc';
    readonly statusOptions = [
        {id: 'active', name: 'Attivo'},
        {id: 'pendingOtp', name: 'OTP da completare'},
        {id: 'pendingOnboardingOtp', name: 'OTP onboarding'},
        {id: 'pendingOnboarding', name: 'Onboarding in corso'},
        {id: 'pendingApprovation', name: 'In attesa di approvazione'},
    ];
    readonly sortOptions = [
        {id: 'firstName asc', name: 'Nome A–Z'},
        {id: 'lastName asc', name: 'Cognome A–Z'},
        {id: 'createdAt desc', name: 'Più recenti'},
        {id: 'createdAt asc', name: 'Più vecchi'},
        {id: 'lastAccess desc', name: 'Ultimo accesso'},
    ];
    skeletonRows = [0, 1, 2, 3, 4, 5, 6, 7];

    private lastLazyEvent: LazyLoadEvent = {first: 0, rows: 30};

    constructor(private userService: UserService, private router: Router) {
    }

    get activeFilterCount(): number {
        return [
            this.selectedStatusIds.length,
            this.sortBy !== 'firstName asc',
        ].filter(Boolean).length;
    }

    get hasActiveQuery(): boolean {
        return !!this.textFilter.trim() || this.activeFilterCount > 0;
    }

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent) {
        if (!this.filtersPanel?.overlayVisible) return;
        const target = event.target as HTMLElement | null;
        if (!target) return;
        if (
            target.closest('.user-filter-popover') ||
            target.closest('.user-filter-trigger') ||
            target.closest('.p-dropdown-panel') ||
            target.closest('.p-multiselect-panel')
        ) {
            return;
        }
        this.filtersPanel.hide();
    }

    async loadAgents(event?: LazyLoadEvent) {
        if (event) {
            this.lastLazyEvent = event;
            this.first = event.first ?? 0;
            this.rows = event.rows ?? 30;
        }

        const params: Record<string, string | number> = {
            $skip: this.lastLazyEvent.first ?? 0,
            $top: this.lastLazyEvent.rows ?? 30,
            userGroupId: 'agent',
        };

        if (this.textFilter.trim()) params['text'] = this.textFilter.trim();
        if (this.selectedStatusIds.length) params['status'] = this.selectedStatusIds.join(',');
        if (this.sortBy) params['$orderby'] = this.sortBy;

        this.loading = true;
        this.agents = [];
        const response = await this.userService.getUsers(params);
        this.agents = (response.data ?? []).map((item) => new User(item));
        this.totalRecords = response.count ?? this.agents.length;
        this.loading = false;
    }

    applyFilters() {
        this.first = 0;
        this.lastLazyEvent = {...this.lastLazyEvent, first: 0};
        this.filtersPanel?.hide();
        void this.loadAgents(this.lastLazyEvent);
    }

    clearAdvancedFilters() {
        this.selectedStatusIds = [];
        this.sortBy = 'firstName asc';
        this.applyFilters();
    }

    resetAll() {
        this.textFilter = '';
        this.clearAdvancedFilters();
    }

    openAgent(row: User) {
        if (row.id) void this.router.navigate(['/admins/agents', row.id]);
    }

    statusLabel(status?: string) {
        return this.statusOptions.find(option => option.id === status)?.name || status || '—';
    }

    statusBadgeClass(status?: string) {
        if (status === 'active') return 'badge-success';
        if (status?.startsWith('pending')) return 'badge-warning';
        return 'badge-unknown';
    }

    statusIcon(status?: string): string {
        switch (status) {
            case 'active': return 'bi-check-circle-fill';
            case 'pendingOtp': return 'bi-shield-lock';
            case 'pendingOnboardingOtp': return 'bi-shield-exclamation';
            case 'pendingOnboarding': return 'bi-hourglass-split';
            case 'pendingApprovation': return 'bi-clock-history';
            default: return 'bi-question-circle';
        }
    }

    initials(user?: User | null) {
        const first = (user?.firstName || '').trim().charAt(0);
        const last = (user?.lastName || '').trim().charAt(0);
        return (first + last).toUpperCase() || (user?.email || '').trim().charAt(0).toUpperCase() || '?';
    }

    async loginAs(email: string, userId?: string) {
        if (!userId) return;
        try {
            const response: any = await this.userService.loginAs(email, userId);
            if (!response.success) {
                let message = 'Errore generico';
                if (response.code === 'USER_NOT_FOUND') message = 'Utente non trovato';
                if (response.code === 'INCORRECT_PASSWORD') message = 'Password errata';
                Swal.fire({icon: 'error', title: message});
            } else {
                setTimeout(() => window.location.reload(), 300);
            }
        } catch (e) {
            console.error('Login error: ', e);
        }
    }
}
